import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

/**
 * SkillScoreBar — Visualización D3 del score total vs máximo teórico.
 *
 * Renderiza una barra SVG horizontal proporcional usando scaleLinear de D3.
 * Compacta (altura = 6px), vive debajo del número de score total del panel.
 *
 * Props:
 *   @param {number} score      - Score actual calculado
 *   @param {number} maxScore   - Score máximo teórico de referencia
 *   @param {string} color      - Color de la barra (hex o CSS variable)
 *   @param {number} [width]    - Ancho en px (default: container natural)
 */
const SkillScoreBar = ({ score = 0, maxScore = 100, color = '#4ade80', width = 160 }) => {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!svgRef.current) return;

        const height  = 6;
        const radius  = 3;
        const padding = { left: 0, right: 0 };

        // Limpiar render anterior
        d3.select(svgRef.current).selectAll('*').remove();

        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height);

        // D3 scaleLinear: dominio [0, maxScore] → rango [0, width]
        const xScale = d3.scaleLinear()
            .domain([0, Math.max(maxScore, 1)]) // evitar división por cero
            .range([padding.left, width - padding.right])
            .clamp(true);

        const barWidth = xScale(score);

        // Track (barra de fondo)
        svg.append('rect')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', width)
            .attr('height', height)
            .attr('rx', radius)
            .attr('ry', radius)
            .attr('fill', 'rgba(156, 163, 175, 0.25)'); // gray-400/25

        // Fill (barra de valor)
        if (barWidth > 0) {
            svg.append('rect')
                .attr('x', 0)
                .attr('y', 0)
                .attr('width', 0)
                .attr('height', height)
                .attr('rx', radius)
                .attr('ry', radius)
                .attr('fill', color)
                .transition()
                .duration(400)
                .ease(d3.easeCubicOut)
                .attr('width', barWidth);
        }
    }, [score, maxScore, color, width]);

    return (
        <svg
            ref={svgRef}
            aria-label={`Score: ${score} de ${maxScore}`}
            role="img"
            style={{ display: 'block', overflow: 'visible' }}
        />
    );
};

export default SkillScoreBar;
